import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HandCoins, Phone, PhoneCall, User, X } from "lucide-react";
import { api } from "@/lib/api";
import { formatPriceHuf } from "@/lib/format";
import { useSiteSettings } from "@/lib/cms";
import { DEFAULT_SITE_SETTINGS } from "@shared/defaults";
import type { MortgageAdvisorSettings } from "@shared/schema";

/** Egy választható lakás a „melyik lakáshoz?" legördülőhöz (`GET /api/units`). */
export type PublicUnitOption = {
  id: string;
  label: string;
  status: "available" | "reserved" | "sold";
  projectId: string;
  projectName: string;
  projectSlug: string;
  priceHuf: number | null;
};

type LeadForm = {
  name: string;
  phone: string;
  email: string;
  unitId: string;
  message: string;
  consent: boolean;
};

const EMPTY_FORM: LeadForm = {
  name: "",
  phone: "",
  email: "",
  unitId: "",
  message: "",
  consent: false,
};

const FALLBACK = DEFAULT_SITE_SETTINGS.mortgageAdvisor ?? {};

/**
 * Feloldja a doboz szövegeit: a mentett beállítás nyer, de a régebbi (a blokk
 * bevezetése előtt mentett) `site_settings` sorokban még nincs
 * `mortgageAdvisor`, ezért mezőnként visszaesünk az alapértékre.
 */
function resolveSettings(
  s: MortgageAdvisorSettings | undefined,
): Required<Omit<MortgageAdvisorSettings, "enabled" | "advisorEmail">> & {
  enabled: boolean;
} {
  return {
    enabled: s?.enabled !== false,
    title: s?.title?.trim() || FALLBACK.title || "",
    subtitle: s?.subtitle?.trim() || FALLBACK.subtitle || "",
    ctaText: s?.ctaText?.trim() || FALLBACK.ctaText || "",
    formIntro: s?.formIntro?.trim() || FALLBACK.formIntro || "",
    advisorName: s?.advisorName?.trim() || FALLBACK.advisorName || "",
    advisorPhone: s?.advisorPhone?.trim() || FALLBACK.advisorPhone || "",
  };
}

/**
 * „Segítünk a hitelügyintézésben" doboz — felhívás + hitelügyintéző
 * elérhetősége, alatta CTA gombra kinyíló visszahívás-kérő űrlap.
 *
 * A lakás oldalakon a `unitId`-vel érkezve az adott lakás alapértelmezetten
 * ki van választva a „melyik lakáshoz szeretne finanszírozást?" mezőben.
 */
export function MortgageAdvisorBox({
  unitId,
  variant = "section",
  className = "",
}: {
  /** Lakás oldalon: ez a lakás lesz az űrlap előre kiválasztott értéke. */
  unitId?: string;
  /**
   * `section` — teljes szélességű sáv (főoldal, a bannerek alatt).
   * `card` — keretezett kártya, oldalsáv/tartalomoszlop szélességben.
   */
  variant?: "section" | "card";
  className?: string;
}) {
  const siteSettings = useSiteSettings();
  const cfg = resolveSettings(siteSettings.mortgageAdvisor);
  const [open, setOpen] = useState(false);

  if (!cfg.enabled) return null;

  const telHref = cfg.advisorPhone.replace(/[^\d+]/g, "");

  return (
    <section
      data-preview-section="mortgage-advisor"
      className={[
        variant === "section"
          ? "bg-brand-900 text-white py-14 lg:py-16"
          : "bg-brand-50 border border-brand-100 p-6 lg:p-8",
        className,
      ].join(" ")}
    >
      <div className={variant === "section" ? "container" : ""}>
        <div
          className={
            variant === "section"
              ? "max-w-5xl mx-auto flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-10"
              : "flex flex-col gap-5"
          }
        >
          {/* Felhívás */}
          <div className={variant === "section" ? "flex-1" : ""}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 grid place-items-center shrink-0 bg-accent text-white">
                <HandCoins size={22} />
              </div>
              <div className="min-w-0">
                <h2
                  className={[
                    "font-heading uppercase tracking-widish text-lg lg:text-xl",
                    variant === "section" ? "text-white" : "text-brand-700",
                  ].join(" ")}
                >
                  {cfg.title}
                </h2>
                <p
                  className={[
                    "mt-2 text-sm leading-relaxed",
                    variant === "section" ? "text-white/70" : "text-brand-500",
                  ].join(" ")}
                >
                  {cfg.subtitle}
                </p>
              </div>
            </div>

            {/* Hitelügyintéző — név + telefon (e-mail cím szándékosan nem
                jelenik meg; az űrlap a központi címre továbbít). */}
            {(cfg.advisorName || cfg.advisorPhone) && (
              <div
                className={[
                  "mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm",
                  // A felhívás-szöveggel egy vonalban (ikon 3rem + gap 0.75rem).
                  variant === "section" ? "lg:ml-[3.75rem]" : "",
                ].join(" ")}
              >
                {cfg.advisorName && (
                  <span
                    className={[
                      "inline-flex items-center gap-2 font-semibold",
                      variant === "section" ? "text-white" : "text-brand-700",
                    ].join(" ")}
                  >
                    <User size={15} className="shrink-0 text-accent" />
                    {cfg.advisorName}
                  </span>
                )}
                {cfg.advisorPhone && (
                  <a
                    href={`tel:${telHref}`}
                    className={[
                      "inline-flex items-center gap-2 hover:underline",
                      variant === "section" ? "text-white" : "text-accent",
                    ].join(" ")}
                  >
                    <Phone size={15} className="shrink-0 text-accent" />
                    {cfg.advisorPhone}
                  </a>
                )}
              </div>
            )}
          </div>

          {/* CTA */}
          <div className={variant === "section" ? "shrink-0" : ""}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className={[
                "btn-primary justify-center",
                // Sávban: mobilon teljes szélesség, desktopon a szöveg mellé.
                // Kártyában végig teljes szélességű, hogy a szűk oszlopban is
                // egyértelmű CTA maradjon.
                variant === "section" ? "w-full lg:w-auto" : "w-full",
              ].join(" ")}
            >
              {open ? (
                <>
                  <X size={14} /> Bezárás
                </>
              ) : (
                <>
                  <PhoneCall size={14} /> {cfg.ctaText}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Kinyíló űrlap */}
        {open && (
          <div
            className={[
              "mt-8",
              variant === "section"
                ? "max-w-5xl mx-auto bg-white text-brand-700 p-6 lg:p-8"
                : "border-t border-brand-200 pt-6",
            ].join(" ")}
          >
            {cfg.formIntro && (
              <p className="text-sm text-brand-500 mb-5">{cfg.formIntro}</p>
            )}
            <MortgageAdvisorForm defaultUnitId={unitId} />
          </div>
        )}
      </div>
    </section>
  );
}

/** A visszahívás-kérő űrlap. A tartalma a központi értesítési címre megy. */
function MortgageAdvisorForm({ defaultUnitId }: { defaultUnitId?: string }) {
  const siteSettings = useSiteSettings();
  const privacyUrl = siteSettings.contact.privacyPolicyUrl;
  const [form, setForm] = useState<LeadForm>({
    ...EMPTY_FORM,
    unitId: defaultUnitId ?? "",
  });
  const [done, setDone] = useState(false);

  // Lakás oldalról navigálva (prev/next lakás) frissüljön az előválasztás.
  useEffect(() => {
    setForm((f) => ({ ...f, unitId: defaultUnitId ?? "" }));
  }, [defaultUnitId]);

  const unitsQuery = useQuery({
    queryKey: ["public-units"],
    queryFn: () => api<PublicUnitOption[]>("/api/units", { withAuth: false }),
    staleTime: 5 * 60_000,
  });

  /**
   * Csak a még megvásárolható lakásokat kínáljuk fel — kivéve, ha a lakás
   * oldalon éppen egy foglalt/eladott lakást néz a látogató: azt megtartjuk,
   * hogy az előválasztás ne tűnjön el a listából.
   */
  const options = useMemo(() => {
    const all = unitsQuery.data ?? [];
    return all.filter(
      (u) => u.status !== "sold" || u.id === defaultUnitId,
    );
  }, [unitsQuery.data, defaultUnitId]);

  const submit = useMutation({
    mutationFn: () =>
      api<{ ok: true; id?: string }>("/api/mortgage-lead", {
        method: "POST",
        withAuth: false,
        body: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          unitId: form.unitId || null,
          message: form.message.trim() || undefined,
          consent: form.consent,
          sourceUrl:
            typeof window !== "undefined" ? window.location.href : undefined,
        },
      }),
    onSuccess: () => {
      setDone(true);
      setForm({ ...EMPTY_FORM, unitId: defaultUnitId ?? "" });
    },
  });

  if (done) {
    return (
      <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
        Köszönjük! Rögzítettük a kérését — hitelügyintéző kollégánk hamarosan
        visszahívja a megadott telefonszámon.
      </div>
    );
  }

  const upd = <K extends keyof LeadForm>(key: K, value: LeadForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (submit.isPending) return;
        submit.mutate();
      }}
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="label text-xs" htmlFor="ma-name">
            Név <span className="text-red-500">*</span>
          </label>
          <input
            id="ma-name"
            className="input"
            required
            autoComplete="name"
            value={form.name}
            onChange={(e) => upd("name", e.target.value)}
          />
        </div>
        <div>
          <label className="label text-xs" htmlFor="ma-phone">
            Telefonszám <span className="text-red-500">*</span>
          </label>
          <input
            id="ma-phone"
            type="tel"
            className="input"
            required
            autoComplete="tel"
            placeholder="+36 ..."
            value={form.phone}
            onChange={(e) => upd("phone", e.target.value)}
          />
        </div>
        <div>
          <label className="label text-xs" htmlFor="ma-email">
            E-mail cím <span className="text-red-500">*</span>
          </label>
          <input
            id="ma-email"
            type="email"
            className="input"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => upd("email", e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="label text-xs" htmlFor="ma-unit">
          Melyik lakáshoz szeretne finanszírozást?{" "}
          <span className="text-brand-400 font-normal normal-case">
            (opcionális)
          </span>
        </label>
        <select
          id="ma-unit"
          className="input"
          value={form.unitId}
          onChange={(e) => upd("unitId", e.target.value)}
        >
          <option value="">— még nem választottam lakást —</option>
          {options.map((u) => (
            <option key={u.id} value={u.id}>
              {/* Ár nélküli lakásnál nincs „· —" utótag — a legördülőben az
                  üres ár csak zaj lenne. */}
              {u.projectName} — {u.label}
              {u.priceHuf != null ? ` · ${formatPriceHuf(u.priceHuf)}` : ""}
            </option>
          ))}
        </select>
        {unitsQuery.isLoading && (
          <p className="mt-1 text-xs text-brand-400">Lakások betöltése…</p>
        )}
      </div>

      <div>
        <label className="label text-xs" htmlFor="ma-message">
          Üzenet{" "}
          <span className="text-brand-400 font-normal normal-case">
            (opcionális)
          </span>
        </label>
        <textarea
          id="ma-message"
          className="input resize-y"
          rows={3}
          placeholder="Pl. mekkora önerővel rendelkezik, mikorra tervezi a vásárlást…"
          value={form.message}
          onChange={(e) => upd("message", e.target.value)}
        />
      </div>

      <label
        htmlFor="ma-consent"
        className="flex items-start gap-2 text-sm text-brand-700 cursor-pointer"
      >
        <input
          id="ma-consent"
          type="checkbox"
          required
          className="mt-0.5"
          checked={form.consent}
          onChange={(e) => upd("consent", e.target.checked)}
        />
        <span>
          Elfogadom az{" "}
          {privacyUrl ? (
            <a
              href={privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 underline hover:text-brand-900"
              onClick={(e) => e.stopPropagation()}
            >
              adatvédelmi tájékoztatót
            </a>
          ) : (
            "adatvédelmi tájékoztatót"
          )}{" "}
          és hozzájárulok adataim kezeléséhez.
          <span className="text-red-500 ml-0.5">*</span>
        </span>
      </label>

      {submit.isError && (
        <div className="text-sm text-red-600">
          Nem sikerült elküldeni a kérést. Kérjük, próbálja újra.
        </div>
      )}

      <button type="submit" className="btn-primary" disabled={submit.isPending}>
        {submit.isPending ? "Küldés…" : "Visszahívást kérek"}
      </button>
    </form>
  );
}
